import React from 'react';
import { Box, FormControlLabel, Checkbox, Typography, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { VolumeVisibility, VolumeMetric } from '../types';

const VOLUME_COLOR = '#4CAF50';

interface VolumeToggleControlsProps {
    visibility: VolumeVisibility;
    onChange: (visibility: VolumeVisibility) => void;
    disabled?: boolean;
    // Per-metric disabled state (e.g., buySellVolume disabled when no trades data)
    disabledMetrics?: Partial<Record<VolumeMetric, boolean>>;
}

export const VolumeToggleControls: React.FC<VolumeToggleControlsProps> = ({
    visibility,
    onChange,
    disabled = false,
    disabledMetrics,
}) => {
    // Check if a specific metric is disabled
    const isMetricDisabled = (metric: VolumeMetric): boolean => {
        if (disabled) return true;
        if (disabledMetrics && disabledMetrics[metric]) return true;
        return false;
    };
    const handleShowVolumeChange = () => {
        onChange({
            ...visibility,
            showVolume: !visibility.showVolume,
        });
    };

    const handleMetricChange = (
        _event: React.MouseEvent<HTMLElement>,
        newMetric: VolumeMetric | null
    ) => {
        if (newMetric !== null) {
            onChange({
                ...visibility,
                metric: newMetric,
            });
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1,
                alignItems: 'center',
                mb: 1,
            }}
        >
            <Typography variant="body2" sx={{ mr: 1, color: 'text.secondary' }}>
                Volume:
            </Typography>
            <FormControlLabel
                control={
                    <Checkbox
                        checked={visibility.showVolume}
                        onChange={handleShowVolumeChange}
                        disabled={disabled}
                        size="small"
                        sx={{
                            color: VOLUME_COLOR,
                            '&.Mui-checked': { color: VOLUME_COLOR },
                            padding: '4px',
                        }}
                    />
                }
                label={<Typography variant="body2">Show</Typography>}
                sx={{ marginRight: 2 }}
            />

            {visibility.showVolume && (
                <ToggleButtonGroup
                    value={visibility.metric}
                    exclusive
                    onChange={handleMetricChange}
                    disabled={disabled}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            padding: '2px 8px',
                            fontSize: '0.75rem',
                        },
                    }}
                >
                    <ToggleButton value="volume" disabled={isMetricDisabled('volume')}>
                        Volume
                    </ToggleButton>
                    <ToggleButton value="tradeCount" disabled={isMetricDisabled('tradeCount')}>
                        Trades
                    </ToggleButton>
                    <ToggleButton value="buySellVolume" disabled={isMetricDisabled('buySellVolume')}>
                        Buy/Sell
                    </ToggleButton>
                </ToggleButtonGroup>
            )}
        </Box>
    );
};
